import { useState, useEffect, useRef, useCallback } from 'react';
import { POLLING_INTERVAL, MAX_POLLING_ATTEMPTS } from '../utils/constants';

interface PollResponse {
  status: string;
  progress?: number;
  result?: any;
  type?: string;
  job_type?: string;
  errors?: Array<{ error: string }>;
  error?: string;
}

const useJobPolling = (apiCall: (jobId: string) => Promise<PollResponse>) => {
  const [status, setStatus] = useState<string>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [jobType, setJobType] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptCountRef = useRef<number>(0);
  const currentJobIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    currentJobIdRef.current = null;
    attemptCountRef.current = 0;
    startTimeRef.current = null;
  }, []);

  const startPolling = useCallback((jobId: string) => {
    if (!jobId) {
      console.error('❌ startPolling called with no jobId');
      return;
    }
    
    if (currentJobIdRef.current === jobId) {
      console.log('⚠️ Already polling this job:', jobId);
      return;
    }

    console.log('🚀 Starting polling for job:', jobId);

    stopPolling();

    currentJobIdRef.current = jobId;
    attemptCountRef.current = 0;
    startTimeRef.current = Date.now();
    
    console.log('🔧 Setting initial polling states...');
    setStatus('processing');
    setProgress(0);
    setError(null);
    setResult(null);
    setJobType(null);

    const poll = async () => {
      try {
        attemptCountRef.current += 1;
        console.log(`🔄 Poll attempt #${attemptCountRef.current} for job:`, jobId);

        const response = await apiCall(jobId);
        console.log('📊 Poll response:', {
          status: response.status,
          progress: response.progress,
          hasResult: !!response.result,
          jobType: response.type || response.job_type
        });

        if (response.status === 'completed') {
          console.log('✅ Job completed detected! Setting final states...');
          const finalResult = response.result || response;
          
          setStatus('completed');
          setProgress(100);
          setResult(finalResult);
          setJobType(response.type || response.job_type);
          
          console.log('🎯 Final states set:', {
            status: 'completed',
            progress: 100,
            hasResult: !!finalResult,
            resultKeys: finalResult ? Object.keys(finalResult) : [],
            jobType: response.type || response.job_type
          });
          
          if (finalResult && typeof finalResult === 'object') {
            console.log('🔍 Result contains:', {
              hasCaseStudy: !!(finalResult.case_study),
              hasMarkdown: !!(finalResult.markdown),
              hasFileKey: !!(finalResult.file_key)
            });
          }
          
          setTimeout(async () => {
            console.log('🔄 Additional poll after completion for redirect assurance...');
            try {
              const finalResponse = await apiCall(jobId);
              console.log('✅ Final poll confirms completion:', finalResponse.status);
            } catch (_err) {
              console.warn('⚠️ Final poll failed, but job is already completed:', _err);
            }
          }, 500);
          
          stopPolling();
          return;
        }

        if (response.status === 'failed') {
          setStatus('failed');
          const errorMessage = response.errors && response.errors.length > 0
            ? response.errors[response.errors.length - 1].error
            : response.error || 'Job failed';
          setError(errorMessage);
          stopPolling();
          return;
        }

        if (response.type || response.job_type) {
          setJobType(response.type || response.job_type);
        }

        const elapsedMs = Date.now() - (startTimeRef.current || Date.now());
        const targetDurationMs = 4 * 60 * 1000;
        
        let estimatedProgress: number;
        if (elapsedMs < 10000) {
          estimatedProgress = Math.min(15, (elapsedMs / 10000) * 15);
        } else {
          const remainingTime = elapsedMs - 10000;
          const remainingDuration = targetDurationMs - 10000;
          const timeRatio = Math.min(1, remainingTime / remainingDuration);
          
          const logProgress = Math.log(1 + timeRatio * 9) / Math.log(10);
          estimatedProgress = 15 + (logProgress * 75);
        }
        
        setProgress(Math.min(90, Math.round(estimatedProgress)));

        if (attemptCountRef.current >= MAX_POLLING_ATTEMPTS) {
          setStatus('failed');
          setError('Request timed out. Please try again.');
          stopPolling();
          return;
        }

      } catch (err) {
        console.error('Polling error:', err);

        if (attemptCountRef.current < 3) {
          console.log(`Retrying after error (attempt ${attemptCountRef.current})...`);
          return;
        }

        setStatus('failed');
        setError((err as Error).message || 'Failed to check job status');
        stopPolling();
      }
    };

    console.log('⏰ Starting immediate first poll...');
    poll();

    console.log(`⏱️ Setting up polling interval (${POLLING_INTERVAL}ms)...`);
    intervalRef.current = setInterval(poll, POLLING_INTERVAL);
  }, [apiCall, stopPolling]);

  const resetPolling = useCallback(() => {
    stopPolling();
    setStatus('idle');
    setProgress(0);
    setError(null);
    setResult(null);
    setJobType(null);
    startTimeRef.current = null;
  }, [stopPolling]);

  const setCompletionState = useCallback((responseData: PollResponse): boolean => {
    console.log('⚡ setCompletionState called with:', {
      status: responseData.status,
      hasResult: !!responseData.result,
      jobType: responseData.type || responseData.job_type
    });
    
    if (responseData.status === 'completed' && responseData.result) {
      setStatus('completed');
      setProgress(100);
      setResult(responseData.result);
      setJobType(responseData.type || responseData.job_type);
      stopPolling();
      return true;
    }
    return false;
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    console.log('🔄 JobPolling state update:', {
      status,
      progress,
      hasResult: !!result,
      jobType,
      currentJobId: currentJobIdRef.current,
      isPolling: intervalRef.current !== null
    });
  }, [status, progress, result, jobType]);

  return {
    status,
    progress,
    error,
    result,
    jobType,
    startPolling,
    stopPolling,
    resetPolling,
    setCompletionState,
    isPolling: intervalRef.current !== null,
    currentJobId: currentJobIdRef.current
  };
};

export default useJobPolling;
